import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, FileText, CheckCircle, XCircle, AlertCircle, Loader2, Download, Paperclip } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { leaveApi, documentApi } from '../../services/api';

interface WorkflowEvent {
  eventType: string;
  timestamp: string;
  actorName: string;
  comment?: string;
}

interface DocumentInfo {
  key: string;
  fileName: string;
  contentType: string;
  documentType: 'MEDICAL_CERTIFICATE' | 'MOTIVATION_LETTER' | 'SUPPORTING_DOCUMENT' | 'OTHER';
}

interface ExcludedDate {
  date: string;
  reason: string;
}

interface LeaveApplicationDetail {
  applicationId: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  departmentName: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  durationType: string;
  status: string;
  motivation: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  recommendation: string | null;
  recommendationComment: string | null;
  recommendedAt: string | null;
  submittedAt: string;
  workflowEvents: WorkflowEvent[];
  hasDocuments?: boolean;
  documentCount?: number;
  documents?: DocumentInfo[];
  // New fields for day calculation
  calculatedDays?: number;
  totalCalendarDays?: number;
  includesWeekends?: boolean;
  excludedDates?: ExcludedDate[];
  approvedDays?: number;
  adjustmentReason?: string;
}

export default function LeaveDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [application, setApplication] = useState<LeaveApplicationDetail | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showCommentModal, setShowCommentModal] = useState<'recommend' | 'notRecommend' | 'approve' | 'decline' | 'cancel' | null>(null);
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);
  // Adjusted days for director approval
  const [adjustedDays, setAdjustedDays] = useState<string>('');
  const [adjustmentReason, setAdjustmentReason] = useState('');

  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) return;

      setIsLoading(true);
      setError('');

      try {
        const response = await leaveApi.getApplication(id) as { application: LeaveApplicationDetail };
        setApplication(response.application);
      } catch (err: any) {
        console.error('Error fetching application:', err);
        setError(err.message || 'Failed to load application details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplication();
  }, [id]);

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <FileText className="h-4 w-4" /> },
      PENDING_RECOMMENDATION: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="h-4 w-4" /> },
      PENDING_APPROVAL: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="h-4 w-4" /> },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="h-4 w-4" /> },
      DECLINED: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="h-4 w-4" /> },
      CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <XCircle className="h-4 w-4" /> },
      CANCELLATION_PENDING: { bg: 'bg-orange-100', text: 'text-orange-800', icon: <AlertCircle className="h-4 w-4" /> },
    };

    const badge = badges[status] || badges.DRAFT;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading application details...</span>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="mx-auto max-w-4xl">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error || 'Application not found'}</p>
        </div>
      </div>
    );
  }

  const canRecommend = hasRole('SUPERVISOR') &&
    application.status === 'PENDING_RECOMMENDATION' &&
    application.supervisorId &&
    user?.userId &&
    application.supervisorId.includes(user.userId);

  const canApprove = hasRole(['DIRECTOR', 'ADMIN', 'BOARD_APPROVER']) && application.status === 'PENDING_APPROVAL';

  const canCancel = user?.userId &&
    application.employeeId.includes(user.userId) &&
    ['PENDING_RECOMMENDATION', 'PENDING_APPROVAL', 'APPROVED'].includes(application.status);

  const handleRecommend = async (recommend: boolean) => {
    if (!id) return;
    setIsProcessing(true);
    setActionError('');

    try {
      await leaveApi.recommendApplication(id, {
        recommendation: recommend ? 'RECOMMENDED' : 'NOT_RECOMMENDED',
        comments: commentText || undefined,
      });
      // Refresh the application
      const response = await leaveApi.getApplication(id) as { application: LeaveApplicationDetail };
      setApplication(response.application);
      setShowCommentModal(null);
      setCommentText('');
    } catch (err: any) {
      console.error('Error recommending:', err);
      setActionError(err.message || 'Failed to submit recommendation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = async (approve: boolean) => {
    if (!id) return;
    setIsProcessing(true);
    setActionError('');

    try {
      if (approve) {
        // Check if days were adjusted
        const parsedAdjustedDays = adjustedDays ? parseFloat(adjustedDays) : undefined;
        const hasAdjustment = parsedAdjustedDays !== undefined &&
          parsedAdjustedDays !== application?.totalDays &&
          parsedAdjustedDays !== application?.calculatedDays;

        await leaveApi.approveApplication(id, {
          comments: commentText || undefined,
          approvedDays: parsedAdjustedDays,
          adjustmentReason: hasAdjustment ? (adjustmentReason || commentText || 'Days adjusted by approver') : undefined,
        });
      } else {
        await leaveApi.declineApplication(id, {
          reason: commentText || 'Application declined',
        });
      }
      // Refresh the application
      const response = await leaveApi.getApplication(id) as { application: LeaveApplicationDetail };
      setApplication(response.application);
      setShowCommentModal(null);
      setCommentText('');
      setAdjustedDays('');
      setAdjustmentReason('');
    } catch (err: any) {
      console.error('Error approving/declining:', err);
      setActionError(err.message || 'Failed to process application');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setIsProcessing(true);
    setActionError('');

    try {
      await leaveApi.cancelApplication(id, {
        reason: commentText || 'Cancelled by applicant',
      });
      // Refresh the application
      const response = await leaveApi.getApplication(id) as { application: LeaveApplicationDetail };
      setApplication(response.application);
      setShowCommentModal(null);
      setCommentText('');
    } catch (err: any) {
      console.error('Error cancelling:', err);
      setActionError(err.message || 'Failed to cancel application');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadDocument = async (doc: DocumentInfo) => {
    setDownloadingDoc(doc.key);
    try {
      const response = await documentApi.getDownloadUrl(doc.key) as { downloadUrl: string };
      // Open the download URL in a new tab
      window.open(response.downloadUrl, '_blank');
    } catch (err: any) {
      console.error('Error downloading document:', err);
      setActionError(err.message || 'Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const formatDocumentType = (type: string) => {
    const labels: Record<string, string> = {
      MEDICAL_CERTIFICATE: 'Medical Certificate',
      MOTIVATION_LETTER: 'Motivation Letter',
      SUPPORTING_DOCUMENT: 'Supporting Document',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const formatLeaveType = (type: string) => {
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leave Application</h1>
            <p className="text-sm text-gray-600">Application #{application.applicationId.slice(0, 8)}</p>
          </div>
          {getStatusBadge(application.status)}
        </div>
      </div>

      {/* Action Error */}
      {actionError && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {/* Application Details */}
      <div className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Application Details</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <User className="mt-0.5 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Applicant</p>
                <p className="font-medium text-gray-900">{application.employeeName}</p>
                <p className="text-sm text-gray-500">{application.departmentName || 'No department'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Leave Type</p>
                <p className="font-medium text-gray-900">{formatLeaveType(application.leaveType)} Leave</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="mt-0.5 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-medium text-gray-900">
                  {application.startDate} - {application.endDate}
                </p>
                <div className="text-sm text-gray-500">
                  {application.approvedDays !== undefined && application.approvedDays !== application.totalDays ? (
                    <div>
                      <span className="line-through text-gray-400">{application.totalDays} day(s)</span>
                      <span className="ml-2 font-medium text-green-600">{application.approvedDays} day(s) approved</span>
                    </div>
                  ) : (
                    <span>{application.totalDays} day(s)</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Submitted</p>
                <p className="font-medium text-gray-900">
                  {application.submittedAt ? new Date(application.submittedAt).toLocaleDateString() : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {application.motivation && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-500">Motivation</p>
            <p className="mt-1 text-gray-900">{application.motivation}</p>
          </div>
        )}

        {/* Day Calculation Details */}
        {(application.excludedDates && application.excludedDates.length > 0) || application.includesWeekends || application.adjustmentReason ? (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Day Calculation Details</p>
            <div className="space-y-2 text-sm">
              {application.totalCalendarDays && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Calendar days:</span>
                  <span className="text-gray-900">{application.totalCalendarDays}</span>
                </div>
              )}
              {application.excludedDates && application.excludedDates.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mt-2">
                  <p className="text-gray-600 font-medium mb-2">Excluded dates:</p>
                  <ul className="space-y-1">
                    {application.excludedDates.map((ed, idx) => (
                      <li key={idx} className="text-gray-500 flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          ed.reason.includes('Holiday') ? 'bg-pink-400' : 'bg-gray-300'
                        }`} />
                        {new Date(ed.date + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}
                        <span className="text-gray-400">- {ed.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {application.includesWeekends && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-2 mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <span>Employee included weekend days in this application</span>
                </div>
              )}
              {application.adjustmentReason && (
                <div className="bg-blue-50 rounded-lg p-3 mt-2">
                  <p className="text-blue-700 font-medium">Approver adjustment:</p>
                  <p className="text-blue-600">{application.adjustmentReason}</p>
                  {application.approvedDays !== undefined && (
                    <p className="text-blue-800 font-medium mt-1">
                      Adjusted to {application.approvedDays} day(s)
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Supporting Documents */}
      {application.hasDocuments && application.documents && application.documents.length > 0 && (
        <div className="card p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Paperclip className="h-5 w-5" />
            Supporting Documents ({application.documents.length})
          </h2>
          <div className="space-y-3">
            {application.documents.map((doc, index) => (
              <div
                key={doc.key || index}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-gray-200">
                    <FileText className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{doc.fileName}</p>
                    <p className="text-sm text-gray-500">{formatDocumentType(doc.documentType)}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadDocument(doc)}
                  disabled={downloadingDoc === doc.key}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {downloadingDoc === doc.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Supervisor Recommendation */}
      {application.recommendation && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Supervisor Recommendation</h2>
          <div className="flex items-center gap-2">
            {application.recommendation === 'RECOMMENDED' ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            <span className={application.recommendation === 'RECOMMENDED' ? 'text-green-600' : 'text-red-600'}>
              {application.recommendation === 'RECOMMENDED' ? 'Recommended' : 'Not Recommended'}
            </span>
            {application.supervisorName && (
              <span className="text-gray-500">by {application.supervisorName}</span>
            )}
          </div>
          {application.recommendationComment && (
            <p className="mt-2 text-gray-600">{application.recommendationComment}</p>
          )}
        </div>
      )}

      {/* Timeline */}
      {application.workflowEvents && application.workflowEvents.length > 0 && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Activity Timeline</h2>
          <div className="space-y-4">
            {application.workflowEvents.map((event, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                    event.eventType === 'APPROVED' ? 'bg-green-100' :
                    event.eventType === 'DECLINED' || event.eventType === 'NOT_RECOMMENDED' ? 'bg-red-100' :
                    event.eventType === 'RECOMMENDED' ? 'bg-blue-100' :
                    'bg-gray-100'
                  }`}>
                    {event.eventType === 'APPROVED' || event.eventType === 'RECOMMENDED' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : event.eventType === 'DECLINED' || event.eventType === 'NOT_RECOMMENDED' ? (
                      <XCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Clock className="h-4 w-4 text-gray-600" />
                    )}
                  </div>
                  {index < application.workflowEvents.length - 1 && (
                    <div className="h-full w-0.5 bg-gray-200" />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <p className="font-medium text-gray-900">{event.eventType.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-gray-500">
                    by {event.actorName} • {new Date(event.timestamp).toLocaleString()}
                  </p>
                  {event.comment && (
                    <p className="mt-1 text-sm text-gray-600">{event.comment}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {(canRecommend || canApprove || canCancel) && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Actions</h2>
          <div className="flex flex-wrap gap-3">
            {canRecommend && (
              <>
                <button
                  onClick={() => setShowCommentModal('recommend')}
                  disabled={isProcessing}
                  className="btn-primary"
                >
                  Recommend
                </button>
                <button
                  onClick={() => setShowCommentModal('notRecommend')}
                  disabled={isProcessing}
                  className="btn-secondary text-red-600 hover:bg-red-50"
                >
                  Do Not Recommend
                </button>
              </>
            )}
            {canApprove && (
              <>
                <button
                  onClick={() => setShowCommentModal('approve')}
                  disabled={isProcessing}
                  className="btn-primary"
                >
                  Approve
                </button>
                <button
                  onClick={() => setShowCommentModal('decline')}
                  disabled={isProcessing}
                  className="btn-secondary text-red-600 hover:bg-red-50"
                >
                  Decline
                </button>
              </>
            )}
            {canCancel && (
              <button
                onClick={() => setShowCommentModal('cancel')}
                disabled={isProcessing}
                className="btn-secondary text-red-600 hover:bg-red-50"
              >
                Cancel Application
              </button>
            )}
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              {showCommentModal === 'recommend' && 'Recommend Application'}
              {showCommentModal === 'notRecommend' && 'Do Not Recommend Application'}
              {showCommentModal === 'approve' && 'Approve Application'}
              {showCommentModal === 'decline' && 'Decline Application'}
              {showCommentModal === 'cancel' && 'Cancel Application'}
            </h3>

            {/* Day adjustment for approve modal - Director only */}
            {showCommentModal === 'approve' && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">Requested days:</span>
                  <span className="font-bold text-gray-900">{application?.calculatedDays || application?.totalDays}</span>
                </div>
                {application?.includesWeekends && (
                  <div className="flex items-center gap-2 text-amber-600 text-sm mb-3">
                    <AlertCircle className="h-4 w-4" />
                    <span>Includes weekend days</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <label className="label text-sm">Adjust leave days (optional)</label>
                  <div className="flex items-center gap-3 mt-1">
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max={application?.totalCalendarDays || application?.totalDays || 30}
                      value={adjustedDays}
                      onChange={e => setAdjustedDays(e.target.value)}
                      className="input w-24"
                      placeholder={String(application?.calculatedDays || application?.totalDays || '')}
                    />
                    <span className="text-sm text-gray-500">days</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to approve as {application?.calculatedDays || application?.totalDays} days
                  </p>
                </div>
                {adjustedDays && parseFloat(adjustedDays) !== (application?.calculatedDays || application?.totalDays) && (
                  <div className="mt-3">
                    <label className="label text-sm">Adjustment reason</label>
                    <input
                      type="text"
                      value={adjustmentReason}
                      onChange={e => setAdjustmentReason(e.target.value)}
                      className="input mt-1"
                      placeholder="e.g., Employee works Saturdays"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="label">
                {showCommentModal === 'decline' || showCommentModal === 'cancel' ? 'Reason (required)' : 'Comments (optional)'}
              </label>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                className="input mt-1 h-24"
                placeholder={
                  showCommentModal === 'decline' ? 'Enter reason for declining...' :
                  showCommentModal === 'cancel' ? 'Enter reason for cancellation...' :
                  'Enter any comments...'
                }
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCommentModal(null);
                  setCommentText('');
                  setAdjustedDays('');
                  setAdjustmentReason('');
                }}
                className="btn-secondary"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (showCommentModal === 'recommend') handleRecommend(true);
                  else if (showCommentModal === 'notRecommend') handleRecommend(false);
                  else if (showCommentModal === 'approve') handleApprove(true);
                  else if (showCommentModal === 'decline') handleApprove(false);
                  else if (showCommentModal === 'cancel') handleCancel();
                }}
                className={`flex items-center gap-2 ${
                  showCommentModal === 'notRecommend' || showCommentModal === 'decline' || showCommentModal === 'cancel'
                    ? 'btn-secondary bg-red-600 text-white hover:bg-red-700'
                    : 'btn-primary'
                }`}
                disabled={isProcessing || ((showCommentModal === 'decline' || showCommentModal === 'cancel') && !commentText)}
              >
                {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
                {showCommentModal === 'approve' && adjustedDays && parseFloat(adjustedDays) !== (application?.calculatedDays || application?.totalDays)
                  ? `Approve (${adjustedDays} days)`
                  : 'Confirm'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
